import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import supabase from '../lib/supabaseClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && session.user.phone) {
          // Normalize phone format
          const phone = session.user.phone.startsWith('+') 
            ? session.user.phone 
            : `+${session.user.phone}`;
          // User is authenticated - query normal_user (RLS allows authenticated role)
          const { data: userData } = await supabase
            .from('normal_user')
            .select('*')
            .eq('phone', phone)
            .maybeSingle();
          
          if (userData) {
            setUser({ 
              id: userData.id, 
              phone: userData.phone,
              name: `${userData.first_name} ${userData.last_name}`,
              email: userData.email,
              role: 'user' 
            });
          }
        }
      } catch (error) {
        // Silent fail - user just won't be logged in
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loginUser = useCallback(async (phone) => {
    try {
      // Get the current session - user is now authenticated after OTP verify
      // This ensures the authenticated JWT is sent with the DB query so RLS allows it
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session. OTP verification may have failed.');
      }

      // Normalize phone - ensure it has + prefix
      const normalizedPhone = phone.startsWith('+') ? phone : `+${phone}`;

      // Now query normal_user with authenticated session - RLS allows authenticated role
      const { data: userData, error } = await supabase
        .from('normal_user')
        .select('*')
        .eq('phone', normalizedPhone)
        .maybeSingle();
      
      if (error) throw error;
      
      if (userData) {
        setUser({ 
          id: userData.id, 
          phone: userData.phone,
          name: `${userData.first_name} ${userData.last_name}`,
          email: userData.email,
          role: 'user' 
        });
        return userData;
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const loginUserWithGoogle = useCallback(async () => {
    // Placeholder for Google Sign-In integration
    const mockUser = { id: 'u1', name: 'Google User', role: 'user', provider: 'google' };
    setUser(mockUser);
    return mockUser;
  }, []);

  const loginMover = useCallback(async (email) => {
    const mockMover = { id: 'm1', name: email || 'Mover', role: 'mover' };
    setUser(mockMover);
    return mockMover;
  }, []);

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
    }
  }, []);

  const value = useMemo(() => ({ user, isAuthenticated: !!user, loading, loginUser, loginUserWithGoogle, loginMover, logout }), [user, loading, loginUser, loginUserWithGoogle, loginMover, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
